import {
  Button,
  Card,
  Col,
  Form,
  Modal,
  Radio,
  Row,
  Spin,
  Table,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useGetUser } from "../hooks/use-get-user";
import Input from "antd/es/input";
import VuiFormActionButton from "../../../components/form-action-button";
import RoleRepository from "../../../repositories/role-repository";
import VuiSelect from "../../../components/select";
import { useCallback, useEffect, useMemo, useState } from "react";
import VuiPageTitle from "../../../components/page-title";
import { useFormUser } from "../hooks/use-form-user";
import { UserFormData, UserPayloadType } from "./interface";
import { convertToSelectValue } from "../../../components/select/function";
import { ENV } from "../../../constants";
import VuiAccessible from "../../../components/accessible";
import VuiModalConfirmation from "../../../components/modal-confirmation";
import { handleBackendError, openNotification } from "../../../utils/helpers";
import VuiButton from "../../../components/button";
import { ColumnsType } from "antd/lib/table";
import UserRepository from "../../../repositories/user-repository";
import UserAssignAssetRepository from "../../../repositories/user-assign-asset-repository";
import { User } from "../../../models/user";
import { AxiosError } from "axios";
import { useInvoiceCount } from "../../../context/invoice";

const { Text } = Typography;

const UserFormModule = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const title = id ? t("pages.user.detail.title") : t("pages.user.add.title");
  const { loadUserData, isOnFetchingUserData, userData } = useGetUser();
  const { submitLoading, onSubmit, deleteLoading, onDelete } = useFormUser();
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showAssignAssetModal, setShowAssignAssetModal] =
    useState<boolean>(false);
  const [transferToUser, setTransferToUser] = useState<any>(null);
  const [transferLoading, setTransferLoading] = useState<boolean>(false);
  const { refreshCounts } = useInvoiceCount();

  const [form] = Form.useForm();

  const onFinish = useCallback(
    async (data: UserFormData) => {
      const normalizeData: UserPayloadType = {
        name: data.name,
        email: data.email,
        phone_number: data.phone_number,
        role_id: data.role_id.value as number,
        is_receive_invoice_copy: data.is_receive_invoice_copy,
        ...(!id ? { url: `${ENV.getHostAppUrl()}/reset-password` } : {}),
        is_active: data.is_active,
      };

      await onSubmit(normalizeData, id);
    },
    [id, onSubmit]
  );

  const handleCloseModalDelete = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (id) {
      await onDelete(id).then(() => {
        setShowDeleteModal(false);
        openNotification(
          "success",
          t("notification.success.deleteItem", {
            item: t("common.text.user"),
          })
        );
        navigate(`/user-role`);
      });
    }
  }, [id, navigate, onDelete, t]);

  useEffect(() => {
    if (id) {
      (async () => {
        await loadUserData(id, {
          with: [
            "roleUser.role",
            "leads",
            "projects",
            "gwCustomers",
            "invoices",
            "maintenances",
            "domains",
          ],
        });
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (id && userData) {
      form.setFieldsValue({
        ...userData,
        role_id: convertToSelectValue(userData.role_user.role),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  const haveAssignAssets = useMemo(() => {
    if (userData) {
      const u: any = userData as User;
      return (
        (u.leads && u.leads.length > 0) ||
        (u.invoices && u.invoices.length > 0) ||
        (u.gwCustomers && u.gwCustomers.length > 0) ||
        (u.maintenances && u.maintenances.length > 0) ||
        (u.domains && u.domains.length > 0)
      );
    }
    return false;
  }, [userData]);

  const assignAssetsSummaryData = useMemo(() => {
    const u: any = userData as User;
    return [
      {
        key: "leads",
        asset: t("common.text.leads"),
        total: Array.isArray(u?.leads) ? u.leads.length : 0,
      },
      {
        key: "maintenance",
        asset: t("common.text.maintenance"),
        total: Array.isArray(u?.maintenances) ? u.maintenances.length : 0,
      },
      {
        key: "domain",
        asset: t("common.text.domain"),
        total: Array.isArray(u?.domains) ? u.domains.length : 0,
      },
      {
        key: "gw_customers",
        asset: t("common.text.gwProduct"),
        total: Array.isArray(u?.gw_customers) ? u.gw_customers.length : 0,
      },
      {
        key: "invoices",
        asset: t("common.text.invoice"),
        total: Array.isArray(u?.invoices) ? u.invoices.length : 0,
      },
    ];
  }, [t, userData]);

  const assignAssetsSummaryColumns: ColumnsType<{
    key: string;
    asset: string;
    total: number;
  }> = useMemo(
    () => [
      {
        title: t("common.text.asset"),
        dataIndex: "asset",
        key: "asset",
      },
      {
        title: t("common.text.total"),
        dataIndex: "total",
        key: "total",
        align: "right",
      },
    ],
    [t]
  );

  const handleTransferAssets = useCallback(async () => {
    if (!id || !transferToUser) return;

    setTransferLoading(true);
    try {
      const payload = {
        assign_to_id: transferToUser.value,
      };
      await UserAssignAssetRepository.assignAssets(id, payload);
      await refreshCounts();
      openNotification("success", t("notification.success.assignAssets"));
      setShowAssignAssetModal(false);
      setTransferToUser(null);
      await loadUserData(id, {
        with: [
          "roleUser.role",
          "leads",
          "projects",
          "gwCustomers",
          "invoices",
          "maintenances",
          "domains",
        ],
      });
    } catch (e) {
      handleBackendError(e as AxiosError);
    } finally {
      setTransferLoading(false);
    }
  }, [id, t, transferToUser, loadUserData, refreshCounts]);

  return (
    <>
      <VuiPageTitle title={title} onBackLink="/user-role">
        {haveAssignAssets && (
          <VuiButton
            title={t("common.button.viewAssignedAssets")}
            buttonProps={{
              onClick: () => {
                setShowAssignAssetModal(true);
              },
            }}
          />
        )}
      </VuiPageTitle>

      <Spin size="large" spinning={isOnFetchingUserData}>
        <Form form={form} layout={"vertical"} onFinish={onFinish}>
          <Row gutter={[16, 16]}>
            <Col md={16} xs={24}>
              <Card title={t("common.text.information")}>
                <Row gutter={[16, 16]}>
                  <Col xs={24}>
                    <Form.Item
                      name="name"
                      label={t("common.form.name.label")}
                      rules={[
                        {
                          required: true,
                          message: t("validation.required", {
                            item: t("common.form.name.label"),
                          }),
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder={t("common.form.name.placeholder")}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      name="email"
                      label={t("common.form.email.label")}
                      rules={[
                        {
                          required: true,
                          message: t("validation.required", {
                            item: t("common.form.email.label"),
                          }),
                        },
                        {
                          type: "email",
                          message: t("validation.email", {
                            item: t("common.form.email.label"),
                          }),
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder={t("common.form.email.placeholder")}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      name="phone_number"
                      label={t("common.form.phoneNumber.label")}
                      rules={[
                        {
                          pattern: new RegExp("^[0-9-()+\\s]*$"),
                          message: t("validation.invalid", {
                            item: t("common.form.phoneNumber.label"),
                          }),
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder={t("common.form.phoneNumber.placeholder")}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <Form.Item
                      name="role_id"
                      label={t("common.form.role.label")}
                      rules={[
                        {
                          required: true,
                          message: t("validation.required", {
                            item: t("common.form.role.label"),
                          }),
                        },
                      ]}
                    >
                      <VuiSelect repository={RoleRepository} />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>

            <Col md={8} xs={24}>
              <Row gutter={[16, 16]}>
                <Col md={24} xs={24}>
                  <Card title={t("common.text.setting")}>
                    <Form.Item
                      label={t("common.form.receiveInvoiceCopy.label")}
                      name="is_receive_invoice_copy"
                      initialValue={false}
                      required
                    >
                      <Radio.Group>
                        <Radio value={false}>{t("common.text.no")}</Radio>
                        <Radio value={true}>{t("common.text.yes")}</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Card>
                </Col>
                <Col md={24} xs={24}>
                  <Card title={t("common.text.active")}>
                    <Form.Item
                      label={t("common.form.activeUser.label")}
                      name="is_active"
                      initialValue={true}
                      required
                    >
                      <Radio.Group>
                        <Radio value={false}>{t("common.text.no")}</Radio>
                        <Radio value={true}>{t("common.text.yes")}</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Card>
                </Col>
              </Row>
            </Col>

            <Col md={16} xs={24}>
              <VuiFormActionButton
                cancelBtnLink="/user-role"
                isLoading={submitLoading}
              />
            </Col>

            {id && (
              <VuiAccessible access="project.destroy">
                <Col xs={24}>
                  <Text
                    className="cursor-pointer"
                    onClick={() => setShowDeleteModal(true)}
                    title={t("common.text.delete")}
                    type="secondary"
                  >
                    {t("common.text.delete")}
                  </Text>
                </Col>
              </VuiAccessible>
            )}
          </Row>
        </Form>
      </Spin>

      <VuiModalConfirmation
        show={showDeleteModal}
        isLoading={deleteLoading}
        onSubmit={handleConfirmDelete}
        onCancel={handleCloseModalDelete}
      />
      <Modal
        title={t("common.text.viewAssignedAssets")}
        centered
        visible={showAssignAssetModal}
        onCancel={() => setShowAssignAssetModal(false)}
        footer={[
          <Button
            key="cancel"
            onClick={() => setShowAssignAssetModal(false)}
            disabled={transferLoading}
          >
            {t("common.button.close", { defaultValue: "Close" })}
          </Button>,
          <Button
            key="transfer"
            type="primary"
            disabled={!transferToUser}
            loading={transferLoading}
            onClick={handleTransferAssets}
          >
            {t("common.button.transferAssets")}
          </Button>,
        ]}
      >
        <Table
          dataSource={assignAssetsSummaryData}
          columns={assignAssetsSummaryColumns}
          rowKey="key"
          pagination={false}
          size="small"
        />
        <Form layout="vertical">
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24}>
              <Form.Item label={t("common.form.assignTo.label")} required>
                <VuiSelect
                  repository={UserRepository}
                  onChange={(val: any) => setTransferToUser(val)}
                  placeholder={t("common.form.assignTo.placeholder")}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default UserFormModule;
